import { useState, useEffect } from "react";
import { SubmitButton } from "../../components/Input";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import StateManagedSelect from "react-select";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";

const TemplateWhatsappNotificationForm = () => {
  const [templateName, setTemplateName] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [templateType, setTemplateType] = useState("1");
  const [file, setfile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [oldFileUrl, setOldFileUrl] = useState("");
  const [fieldList, setFieldlist] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedButtonOptions, setSelectedButtonOptions] = useState([]);

  // ✅ New state for WhatsApp sender list
  const [senderList, setSenderList] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);

  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();

  // ✅ Fetch WhatsApp sender list from API
  const fetchSenderList = async (fields = []) => {
    try {
      const response = await axios.get(`${API_URL}settings/emailconfig/dropdown`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const whatsappSenders =
        response.data?.data?.whatsapp?.map((item) => ({
          value: Number(item.value), // convert to number to match sender_type
          label: `${item.label} (${item.sender})`,
          sender: item.sender,
        })) || [];

      setSenderList(whatsappSenders);

      // ✅ After sender list is loaded, fetch template (only if editing)
      if (id) {
        fetchTemplates(id, whatsappSenders, fields);
      }
    } catch (error) {
      console.error("Error fetching WhatsApp sender list:", error);
    }
  };

  const fetchTemplates = async (id, whatsappSenders = [], fields = []) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}settings/template/whatsapp/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = response.data.data;
      setTemplateName(data.templatename);
      setCampaignName(data.campaign);
      setTemplateType(data.template_type);
      setFileName(data.file_name || "");
      setOldFileUrl(data.file_url || "");

      // ✅ Set selected sender properly
      if (data.sender_type && whatsappSenders.length > 0) {
        const foundSender = whatsappSenders.find(
          (opt) => opt.value === Number(data.sender_type)
        );
        setSelectedSender(foundSender || null);
      }

      if (data.message) {
        const selected = data.message
          .split(",")
          .map((fieldValue) => fields.find((option) => option.value === fieldValue))
          .filter(Boolean);
        setSelectedOptions(selected);
      }

      if (data.button_message) {
        const selectedButton = data.button_message
          .split(",")
          .map((fieldValue) => fields.find((option) => option.value === fieldValue))
          .filter(Boolean);
        setSelectedButtonOptions(selectedButton);
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFieldList = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}settings/template/email/dropdown`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const formattedField = response.data.data.map((field) => ({
        value: field.field_name,
        label: field.field_description,
      }));
      setFieldlist(formattedField);
      return formattedField;
    } catch (error) {
      console.error("Error fetching templates:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load field list and sender list sequentially
    const loadData = async () => {
      const fields = await fetchFieldList();
      await fetchSenderList(fields);
    };
    loadData();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setfile(file);
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;
    setTemplateType(value);
    if (value === "1") {
      setFileName("");
      setOldFileUrl("");
    }
  };

  const addDropdown = () => setSelectedOptions([...selectedOptions, { value: "", label: "" }]);
  const removeDropdown = (index) =>
    setSelectedOptions(selectedOptions.filter((_, i) => i !== index));
  const handleDropdownChange = (selected, index) => {
    const updated = [...selectedOptions];
    updated[index] = selected;
    setSelectedOptions(updated);
  };

  const addButtonDropdown = () => setSelectedButtonOptions([...selectedButtonOptions, { value: "", label: "" }]);
  const removeButtonDropdown = (index) =>
    setSelectedButtonOptions(selectedButtonOptions.filter((_, i) => i !== index));
  const handleButtonDropdownChange = (selected, index) => {
    const updated = [...selectedButtonOptions];
    updated[index] = selected;
    setSelectedButtonOptions(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      templatename: templateName,
      campaign: campaignName,
      template_type: templateType,
      file_url: file,
      file_name: fileName,
      message: selectedOptions.map((item) => item.value).join(","),
      button_message: selectedButtonOptions.map((item) => item.value).join(","),
      sender_type: selectedSender ? selectedSender.value : "", // ✅ include sender
    };

    try {
      const url = id
        ? `${API_URL}settings/template/whatsapp/save?id=${id}`
        : `${API_URL}settings/template/whatsapp/save`;

      const response = await axios.post(url, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.success === true) {
        showToast.success(response.data.message);
        navigate("/Settings/TemplateWhatsappNotification");
      }
    } catch (error) {
      console.error("Error saving template:", error);
      showToast.error("There was an error saving the template");
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content">
        <div className="panel panel-default with-rounded-corner with-shadow">
          <form onSubmit={handleSubmit} className="form" encType="multipart/form-data">
            <input type="hidden" name="id" value={id} />
            <input type="hidden" name="old_file_url" value={oldFileUrl} />

            <div className="panel-heading border-0">
              <span className="panel-title">
                {id ? "Edit " : "Add "} Whatsapp Template
              </span>
            </div>

            <div className="panel-body">
              <div className="row align-items-center">
                {/* Template Name */}
                <div className="col-md-4 form-group">
                  <label>Template Name</label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Enter Template Name"
                    className="form-control"
                  />
                </div>

                {/* Campaign Name */}
                <div className="col-md-4 form-group">
                  <label>Campaign Name</label>
                  <input
                    type="text"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="Enter Campaign Name"
                    className="form-control"
                  />
                </div>

                {/* WhatsApp Sender Dropdown */}
                <div className="col-md-4 form-group">
                  <label>WhatsApp Sender</label>
                  <StateManagedSelect
                    options={senderList}
                    value={selectedSender}
                    onChange={setSelectedSender}
                    placeholder="Select WhatsApp Sender"
                    isClearable
                  />
                </div>

                {/* Template Type */}
                <div className="col-md-4 form-group">
                  <label>Template Type</label>
                  <select
                    value={templateType}
                    onChange={handleTypeChange}
                    className="form-control"
                  >
                    <option value="1">Text</option>
                    <option value="2">Image</option>
                    <option value="3">Video</option>
                    <option value="4">File</option>
                  </select>
                </div>

                {/* File Upload if not text */}
                {templateType !== "1" && (
                  <>
                    <div className="col-md-4 form-group">
                      <label>File Name</label>
                      <input
                        type="text"
                        value={fileName}
                        onChange={(e) => setFileName(e.target.value)}
                        placeholder="Enter Uploaded File Name"
                        className="form-control"
                      />
                    </div>
                    <div className="col-md-4 form-group">
                      <label>Upload File</label>
                      <input
                        type="file"
                        onChange={handleFileChange}
                        className="form-control"
                      />
                    </div>
                    {oldFileUrl && (
                      <div className="col-md-3 file-holder">
                        <img src={oldFileUrl} alt="preview" width="50px" />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Dynamic Fields Table */}
              <div className="row clonelist">
                {/* Message Fields */}
                <div className="col-md-4">
                  <label style={{ fontWeight: "bold" }}>Message Fields</label>
                  <table className="table m-b-0">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center", width: "30%" }}>
                          Name
                        </th>
                        <th style={{ textAlign: "center", width: "10%" }}>
                          Delete
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOptions.map((selected, index) => (
                        <tr key={index}>
                          <td>
                            <StateManagedSelect
                              options={fieldList}
                              value={selected}
                              onChange={(selected) =>
                                handleDropdownChange(selected, index)
                              }
                              placeholder="select..."
                            />
                          </td>
                          <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                            <button
                              type="button"
                              className="btn btn-danger btn-gradient delete_row btn-alt pt5 pb5 mn"
                              onClick={() => removeDropdown(index)}
                            >
                              X Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan="2">
                          <button
                            type="button"
                            className="addPick btn btn-success"
                            onClick={addDropdown}
                          >
                            Add Data
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Button Message Fields */}
                <div className="col-md-4">
                  <label style={{ fontWeight: "bold" }}>Button Message Fields</label>
                  <table className="table m-b-0">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center", width: "30%" }}>
                          Name
                        </th>
                        <th style={{ textAlign: "center", width: "10%" }}>
                          Delete
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedButtonOptions.map((selected, index) => (
                        <tr key={index}>
                          <td>
                            <StateManagedSelect
                              options={fieldList}
                              value={selected}
                              onChange={(selected) =>
                                handleButtonDropdownChange(selected, index)
                              }
                              placeholder="select..."
                            />
                          </td>
                          <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                            <button
                              type="button"
                              className="btn btn-danger btn-gradient delete_row btn-alt pt5 pb5 mn"
                              onClick={() => removeButtonDropdown(index)}
                            >
                              X Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan="2">
                          <button
                            type="button"
                            className="addPick btn btn-success"
                            onClick={addButtonDropdown}
                          >
                            Add Data
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <SubmitButton
                pageBack={"/Settings/TemplateWhatsappNotification"}
                handleSubmit={handleSubmit}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TemplateWhatsappNotificationForm;
